import { Component } from '@theme/component';

/**
 * Botón "Comprar ahora": agrega el producto al carrito y lleva directo al checkout de Shopify,
 * donde el cliente elige cómo pagar (tarjeta con Shopify Payments, Mercado Pago, PayPal, etc.).
 *
 * Sin JavaScript el botón envía el formulario del producto con return_to=/checkout, así que
 * también funciona mientras carga el módulo.
 *
 * @typedef {object} ElifeBuyNowRefs
 * @property {HTMLButtonElement} button - El botón de compra.
 * @property {HTMLElement | undefined} error - Donde se muestra el error si no se pudo agregar.
 *
 * @extends Component<ElifeBuyNowRefs>
 */
class ElifeBuyNow extends Component {
  requiredRefs = ['button'];

  /** @type {AbortController | undefined} */
  #controller;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /**
   * Agrega al carrito lo que tenga el formulario (variante y cantidad) y redirige al checkout.
   *
   * @param {MouseEvent} event - El clic en el botón.
   */
  async buyNow(event) {
    const { button, error } = this.refs;
    const form = button.form;
    if (!form) return;

    event.preventDefault();
    if (button.getAttribute('aria-busy') === 'true') return;

    this.#controller?.abort();
    this.#controller = new AbortController();

    const body = new FormData(form);
    body.delete('return_to');

    button.setAttribute('aria-busy', 'true');
    error?.setAttribute('hidden', '');

    try {
      const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
        signal: this.#controller.signal,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || data.message || '');
      }

      window.location.assign(this.dataset.checkoutUrl || '/checkout');
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;

      button.removeAttribute('aria-busy');
      if (error) {
        error.textContent = (err instanceof Error && err.message) || this.dataset.errorMessage || '';
        error.removeAttribute('hidden');
      }
    }
  }
}

if (!customElements.get('elife-buy-now')) {
  customElements.define('elife-buy-now', ElifeBuyNow);
}
